import { request as httpRequest, Agent as HttpAgent } from 'node:http'
import { request as httpsRequest, Agent as HttpsAgent } from 'node:https'
import { existsSync, readFileSync, writeFileSync, mkdirSync, chmodSync } from 'node:fs'
import { resolve, dirname } from 'node:path'
import { homedir } from 'node:os'

// DRIFTLESS_CONFIG_DIR overrides the location (used by tests and the hermetic
// harness); defaults to ~/.driftless. Every CLI-side state file — config,
// topic cache, hook flag/seen/touched markers — lives under this one root, so
// pointing it at a temp dir makes a run fully isolated.
const CONFIG_DIR = process.env['DRIFTLESS_CONFIG_DIR'] || resolve(homedir(), '.driftless')
const CONFIG_PATH = resolve(CONFIG_DIR, 'config.json')

export function driftlessHome(): string {
  return CONFIG_DIR
}

// One registered workspace the user can switch between. The *active* workspace
// is mirrored into the flat api_key/workspace_slug/workspace_id fields below, so
// the rest of the CLI (loadApiKey, repo resolution) needs no changes — switching
// just copies the chosen entry into those flat fields.
export interface WorkspaceEntry {
  slug: string
  id?: string
  name?: string
  api_key: string
}

type Config = Record<string, unknown> & {
  api_key?: string
  api_url?: string
  workspace_slug?: string
  workspace_id?: string
  active_project?: { id: string; name?: string }
  workspaces?: WorkspaceEntry[]
  active_workspace?: string
}

function readConfig(): Config {
  try {
    if (existsSync(CONFIG_PATH)) {
      return JSON.parse(readFileSync(CONFIG_PATH, 'utf8')) as Config
    }
  } catch {
    // ignore
  }
  return {}
}

/**
 * Securely persist config.json — the file holds the raw API key, so it must be
 * owner-only: dir 0700, file 0600. Merges over existing config (never clobbers).
 */
export function persistConfig(partial: Record<string, unknown>): void {
  const dir = dirname(CONFIG_PATH)
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true, mode: 0o700 })
  try { chmodSync(dir, 0o700) } catch { /* best-effort on non-POSIX */ }
  const next = { ...(readConfig() as Record<string, unknown>), ...partial }
  writeFileSync(CONFIG_PATH, JSON.stringify(next, null, 2) + '\n', { mode: 0o600 })
  try { chmodSync(CONFIG_PATH, 0o600) } catch { /* best-effort on non-POSIX */ }
}

/** Workspace identity cached from a previous successful `/me` (login/init/sync). */
export function getCachedWorkspace(): { slug?: string; workspaceId?: string } {
  const c = readConfig()
  return { slug: c.workspace_slug, workspaceId: c.workspace_id }
}

/**
 * Persist the resolved workspace identity without clobbering api_key/api_url.
 * Best-effort: a failure here must never break the calling command.
 */
export function saveWorkspaceToConfig(slug: string, workspaceId?: string): void {
  if (!slug) return
  try {
    const c = readConfig()
    if (c.workspace_slug === slug && (workspaceId === undefined || c.workspace_id === workspaceId)) {
      return
    }
    c.workspace_slug = slug
    if (workspaceId !== undefined) c.workspace_id = workspaceId
    const dir = dirname(CONFIG_PATH)
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true })
    writeFileSync(CONFIG_PATH, JSON.stringify(c, null, 2) + '\n')
  } catch {
    // best effort — resolution still works via /me
  }
}

/** All workspaces registered in the local config (powers `workspace list`). */
export function listRegisteredWorkspaces(): WorkspaceEntry[] {
  return readConfig().workspaces ?? []
}

/** Add or update (by slug) a workspace in the local registry. */
export function upsertWorkspace(entry: WorkspaceEntry): void {
  const list = (readConfig().workspaces ?? []).filter((w) => w.slug !== entry.slug)
  list.push(entry)
  persistConfig({ workspaces: list })
}

/** Remove a workspace from the registry by slug. */
export function removeWorkspace(slug: string): void {
  const list = (readConfig().workspaces ?? []).filter((w) => w.slug !== slug)
  persistConfig({ workspaces: list })
}

/**
 * Make a registered workspace active: mirror its key/slug/id into the flat
 * fields the rest of the CLI already reads (loadApiKey, repo resolution), so
 * switching needs no other changes. Returns the entry, or null if not registered.
 */
export function activateWorkspace(slug: string): WorkspaceEntry | null {
  const entry = (readConfig().workspaces ?? []).find((w) => w.slug === slug)
  if (!entry) return null
  persistConfig({
    api_key: entry.api_key,
    workspace_slug: entry.slug,
    workspace_id: entry.id,
    active_workspace: entry.slug,
  })
  return entry
}

/**
 * Clear the cached active workspace identity (the flat slug/id mirror) without
 * touching the registry or the api_key. Used to REPAIR a stale active slug — one
 * the current key is not authorized for — so the next resolution falls through to
 * the credential's real workspace via /me instead of replaying a slug that 403s.
 * Best-effort: a failure here must never break the calling command.
 */
export function clearActiveWorkspace(): void {
  try {
    const c = readConfig()
    delete c.workspace_slug
    delete c.workspace_id
    delete c.active_workspace
    const dir = dirname(CONFIG_PATH)
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true })
    writeFileSync(CONFIG_PATH, JSON.stringify(c, null, 2) + '\n')
  } catch {
    // best effort — resolution still works via /me
  }
}

// ─── Authoritative workspace reconciliation (key-agnostic) ────────────────────
//
// The cached active slug (set by `workspace switch` / `login` / a previous /me)
// can go STALE relative to the CURRENT api key: the key was rotated to a
// different workspace, the membership was dropped, or the config was hand-edited.
// A stale slug silently produces `/workspaces/<slug>` 403 ("does not match the
// API key") before any real command runs. The fix is to treat `/me/workspaces`
// (the list the key can ACTUALLY reach) as the source of truth and reconcile the
// cached slug against it instead of trusting the cache blindly.

/** One workspace the current credential is authorized to reach (from /me/workspaces). */
export interface AuthorizedWorkspace {
  slug: string
  id?: string
  workspace_id?: string
  name?: string
}

/**
 * Fetch the workspaces the CURRENT api key can actually use. This is the
 * authoritative list — `workspace list/current/resolve` must never surface or
 * select a slug absent from it. Returns null (not []) when the call could not be
 * made (offline/timeout/error) so callers can DISTINGUISH "the key has no
 * workspaces" from "we couldn't ask" and degrade gracefully in the latter.
 */
export async function fetchAuthorizedWorkspaces(opts?: RequestOpts): Promise<AuthorizedWorkspace[] | null> {
  try {
    const list = (await request('GET', '/me/workspaces', undefined, {
      timeoutMs: 8000,
      retries: 1,
      ...opts,
    })) as Array<Record<string, unknown>>
    if (!Array.isArray(list)) return null
    return list
      .filter((w) => typeof w?.['slug'] === 'string')
      .map((w) => ({
        slug: w['slug'] as string,
        id: typeof w['id'] === 'string' ? (w['id'] as string) : undefined,
        workspace_id: typeof w['workspace_id'] === 'string' ? (w['workspace_id'] as string) : undefined,
        name: typeof w['name'] === 'string' ? (w['name'] as string) : undefined,
      }))
  } catch {
    return null
  }
}

/**
 * The outcome of reconciling a cached active slug against the key's authorized
 * list. `slug` is the workspace to actually use (undefined → caller must pick /
 * error); `action` records what happened so the caller can message the user and
 * persist the repair.
 *  - ok           — the cached slug is authorized; use it as-is.
 *  - repaired     — cached slug was stale; the key has exactly ONE workspace, so
 *                   fall back to it unambiguously.
 *  - ambiguous    — cached slug was stale and the key has SEVERAL workspaces;
 *                   we cannot guess — clear it and point the user at `switch`.
 *  - cleared      — cached slug was stale and the key has NO workspaces at all.
 *  - unknown      — we could not fetch the authorized list (offline); leave the
 *                   cache untouched and let the caller proceed best-effort.
 */
export interface ActiveWorkspaceReconciliation {
  slug?: string
  action: 'ok' | 'repaired' | 'ambiguous' | 'cleared' | 'unknown'
  authorized: string[]
}

/**
 * PURE decision: given the cached active slug and the key's authoritative list,
 * decide the usable slug. No I/O — fully unit-testable. `authorized === null`
 * means the list could not be fetched (offline), so we never destroy the cache.
 */
export function reconcileActiveWorkspace(
  cachedActive: string | undefined,
  authorized: AuthorizedWorkspace[] | null,
): ActiveWorkspaceReconciliation {
  if (authorized === null) {
    // Couldn't ask — trust the cache, don't repair on a network blip.
    return { slug: cachedActive, action: 'unknown', authorized: [] }
  }
  const slugs = authorized.map((w) => w.slug)
  // Cached slug is genuinely usable → keep it.
  if (cachedActive && slugs.includes(cachedActive)) {
    return { slug: cachedActive, action: 'ok', authorized: slugs }
  }
  // No cached slug yet, but the key has exactly one workspace → adopt it.
  if (!cachedActive) {
    if (slugs.length === 1) return { slug: slugs[0], action: 'repaired', authorized: slugs }
    return { slug: undefined, action: slugs.length === 0 ? 'cleared' : 'ambiguous', authorized: slugs }
  }
  // Cached slug is STALE (not in the authorized list) — repair it.
  if (slugs.length === 1) return { slug: slugs[0], action: 'repaired', authorized: slugs }
  // EMPTY list with a cached slug: NOT proof of revocation. An ownerless agent
  // key (created_by NULL) gets [] from /me/workspaces on older APIs even though
  // it reaches its bound workspace — so trust the cache and proceed best-effort;
  // a truly unauthorized workspace still fails closed server-side (403).
  if (slugs.length === 0) return { slug: cachedActive, action: 'unknown', authorized: slugs }
  return { slug: undefined, action: 'ambiguous', authorized: slugs }
}

function loadApiKey(): string | null {
  // 1. Env var
  const envKey = process.env['DRIFTLESS_API_KEY']
  if (envKey) return envKey

  // 2. Config file
  try {
    if (existsSync(CONFIG_PATH)) {
      const config = JSON.parse(readFileSync(CONFIG_PATH, 'utf8'))
      return config.api_key || null
    }
  } catch {
    // ignore
  }

  return null
}

const DEFAULT_URL = 'https://api.driftless.icu/api/v1'

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

function getBaseUrl(): string {
  const envUrl = process.env['DRIFTLESS_API_URL']
  if (envUrl) {
    const clean = stripTrailingSlash(envUrl)
    return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
  }
  // Try config file
  try {
    if (existsSync(CONFIG_PATH)) {
      const config = JSON.parse(readFileSync(CONFIG_PATH, 'utf8'))
      return config.api_url || DEFAULT_URL
    }
  } catch {
    // ignore
  }
  return DEFAULT_URL
}

function parseError(e: Error): string {
  const msg = e.message
  // Try to extract full JSON error body
  const jsonMatch = msg.match(/\{[\s\S]*\}/)
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0])
      const parts: string[] = []
      if (parsed.message) parts.push(parsed.message)
      // Secret-scan gate: the server blocks a write that carries a credential.
      // Surface the override path (covers fields the local pre-flight didn't
      // assemble, and non-CLI callers reading this code).
      if (parsed.code === 'SECRET_DETECTED') parts.push('re-run with --allow-secrets to write anyway')
      if (parsed.request_id) parts.push(`request_id: ${parsed.request_id}`)
      if (parsed.retryable !== undefined) parts.push(`retryable: ${parsed.retryable ? 'yes' : 'no'}`)
      if (parsed.endpoint) parts.push(`endpoint: ${parsed.endpoint}`)
      if (parts.length > 0) return parts.join(' | ')
    } catch { /* ignore */ }
  }
  // HTTP status extraction
  const statusMatch = msg.match(/HTTP (\d+):/)
  if (statusMatch) {
    const code = parseInt(statusMatch[1], 10)
    if (code === 500) return 'Internal server error — the API encountered an unexpected issue'
    if (code === 401) return 'Authentication failed — check your API key'
    if (code === 403) return 'Access denied — your API key lacks permission'
    if (code === 404) return 'Not found — the resource does not exist'
    if (code === 429) return 'Rate limited — too many requests, try again later'
    return `Server error (HTTP ${code})`
  }
  return msg
}

export interface RequestOpts {
  /** Abort the request if no response within this many ms. */
  timeoutMs?: number
  /** Extra attempts on transient failure (network/timeout/5xx). Default 0. */
  retries?: number
  /** Override the API key for this one request (e.g. validate a key on
   *  `workspace add`, or target another workspace). Falls back to the active key. */
  apiKey?: string
  /** Extra headers for this one request (e.g. X-Driftless-Session for delivery
   *  traceability). Merged over the defaults; cannot override X-API-Key. */
  headers?: Record<string, string>
}

// Keep-alive agents (F4.2): a single CLI invocation often makes several API
// calls (e.g. `project card next` then resolving its context bundle). Reusing
// one TCP/TLS connection across them removes a full handshake per call. Sockets
// are kept warm for the life of the process; the CLI exits when work is done,
// so idle sockets never linger. `maxSockets` bounds concurrency for fan-out
// commands without flooding the API.
const httpAgent = new HttpAgent({ keepAlive: true, maxSockets: 16 })
const httpsAgent = new HttpsAgent({ keepAlive: true, maxSockets: 16 })

// Profiling mode (F0.2): when DRIFTLESS_PROFILE is set, every wire call records
// method, path, duration, response bytes and outcome. This is the CLI half of
// the performance harness (see docs/performance/gates.md) — it surfaces payload
// size and per-call latency without external tooling. Entries are printed to
// stderr at process exit so they never pollute a command's stdout/JSON output.
export interface ProfileEntry {
  method: string
  path: string
  ms: number
  bytes: number
  outcome: 'ok' | 'error'
}
const profileEntries: ProfileEntry[] = []
const stageMarks: { stage: string; ms: number }[] = []

export function profilingEnabled(): boolean {
  return !!process.env['DRIFTLESS_PROFILE']
}

export function getProfile(): readonly ProfileEntry[] {
  return profileEntries
}

// Record a lifecycle-stage timing (startup, workspace resolution, git, render,
// total). The entrypoint marks the coarse stages; HTTP is captured per call in
// singleRequest. A stage is a no-op unless profiling is on.
export function profileMark(stage: string, ms: number): void {
  if (profilingEnabled()) stageMarks.push({ stage, ms })
}

export function printProfileSummary(): void {
  if (!profileEntries.length && !stageMarks.length) return
  const json = process.env['DRIFTLESS_PROFILE'] === 'json'
  const httpMs = profileEntries.reduce((s, e) => s + e.ms, 0)
  const bytes = profileEntries.reduce((s, e) => s + e.bytes, 0)
  // Total wall time = time since the process started (node boot + import +
  // command), captured at exit so it holds even when a command exits internally.
  const totalMs = process.uptime() * 1000
  if (json) {
    process.stderr.write(JSON.stringify({ profile: { stages: stageMarks, http: profileEntries, totals: { httpMs, bytes, calls: profileEntries.length, totalMs } } }) + '\n')
    return
  }
  process.stderr.write(`\n[driftless profile] ${profileEntries.length} HTTP call(s), ${httpMs.toFixed(1)}ms on the wire, ${bytes} bytes, ${totalMs.toFixed(1)}ms total\n`)
  for (const m of stageMarks) {
    process.stderr.write(`  stage ${m.stage.padEnd(12)} ${m.ms.toFixed(1).padStart(7)}ms\n`)
  }
  for (const e of profileEntries) {
    const flag = e.outcome === 'error' ? ' ✗' : ''
    process.stderr.write(`  ${e.method.padEnd(6)} ${e.ms.toFixed(1).padStart(7)}ms  ${String(e.bytes).padStart(7)}B  ${e.path}${flag}\n`)
  }
}

if (profilingEnabled()) {
  process.on('exit', printProfileSummary)
}

function singleRequest(
  method: string,
  path: string,
  body: unknown,
  timeoutMs?: number,
  explicitKey?: string,
  extraHeaders?: Record<string, string>,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const baseUrl = getBaseUrl()
    const fullUrl = `${baseUrl}${path}`
    const url = new URL(fullUrl)
    const isHttps = url.protocol === 'https:'
    const fn = isHttps ? httpsRequest : httpRequest
    const agent = isHttps ? httpsAgent : httpAgent
    const startedAt = profilingEnabled() ? process.hrtime.bigint() : 0n
    const record = (bytes: number, outcome: 'ok' | 'error') => {
      if (!startedAt) return
      const ms = Number(process.hrtime.bigint() - startedAt) / 1e6
      profileEntries.push({ method, path, ms, bytes, outcome })
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(extraHeaders ?? {}),
    }

    const apiKey = explicitKey || loadApiKey()
    if (apiKey) {
      headers['X-API-Key'] = apiKey
    }

    const req = fn(
      fullUrl,
      { method, headers, agent },
      (res) => {
        let data = ''
        res.on('data', (chunk: Buffer) => (data += chunk.toString()))
        res.on('end', () => {
          if (res.statusCode && res.statusCode >= 400) {
            record(Buffer.byteLength(data), 'error')
            reject(new Error(`HTTP ${res.statusCode}: ${data}`))
            return
          }
          record(Buffer.byteLength(data), 'ok')
          try {
            resolve(JSON.parse(data))
          } catch {
            resolve(data)
          }
        })
      },
    )

    if (timeoutMs && timeoutMs > 0) {
      req.setTimeout(timeoutMs, () => {
        req.destroy(new Error(`Request timed out after ${timeoutMs}ms`))
      })
    }

    req.on('error', (e) => {
      record(0, 'error')
      reject(new Error(`Connection failed: ${e.message}`))
    })
    if (body) req.write(JSON.stringify(body))
    req.end()
  })
}

// A 4xx is a definitive answer (auth/permission/not found) — retrying won't help.
// Only network errors, timeouts, and 5xx are worth a second attempt.
function isTransient(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return !/HTTP 4\d\d:/.test(msg)
}

// Method-aware defaults so every endpoint is protected from a hung socket
// (the failure class behind the original /me bug), without each call site
// opting in. GET is idempotent → safe to retry a transient failure once;
// POST/PUT/PATCH/DELETE are not auto-retried (a timed-out write may have
// landed). Explicit opts always win.
function defaultsFor(method: string): { timeoutMs: number; retries: number } {
  const isGet = method === 'GET'
  return {
    timeoutMs: isGet ? 15_000 : 20_000,
    retries: isGet ? 1 : 0,
  }
}

async function request(
  method: string,
  path: string,
  body?: unknown,
  opts?: RequestOpts,
): Promise<unknown> {
  const d = defaultsFor(method)
  const timeoutMs = opts?.timeoutMs ?? d.timeoutMs
  const retries = opts?.retries ?? d.retries
  let lastErr: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await singleRequest(method, path, body, timeoutMs, opts?.apiKey, opts?.headers)
    } catch (e) {
      lastErr = e
      if (attempt === retries || !isTransient(e)) break
    }
  }
  throw lastErr
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOpts) =>
    request('GET', path, undefined, opts) as Promise<T>,
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request('POST', path, body, opts) as Promise<T>,
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request('PUT', path, body, opts) as Promise<T>,
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request('PATCH', path, body, opts) as Promise<T>,
  delete: <T = unknown>(path: string, opts?: RequestOpts) =>
    request('DELETE', path, undefined, opts) as Promise<T>,
}

export function getApiUrl(): string {
  return getBaseUrl()
}

export function getApiKey(): string | null {
  return loadApiKey()
}

export function formatError(e: Error): string {
  return parseError(e)
}

export interface ErrorMeta {
  code?: string
  statusCode?: number
  endpoint?: string
  retryable?: boolean
  request_id?: string
}

// Pull the structured fields out of an API error so telemetry can segment by
// code / status / endpoint instead of free-text. Falls back to the HTTP status
// embedded in the thrown `Error("HTTP <n>: …")` when there is no JSON body.
export function parseErrorMeta(e: unknown): ErrorMeta {
  const msg = e instanceof Error ? e.message : String(e)
  const meta: ErrorMeta = {}
  const jsonMatch = msg.match(/\{[\s\S]*\}/)
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0])
      if (typeof parsed.code === 'string') meta.code = parsed.code
      if (typeof parsed.statusCode === 'number') meta.statusCode = parsed.statusCode
      if (typeof parsed.endpoint === 'string') meta.endpoint = parsed.endpoint
      if (typeof parsed.retryable === 'boolean') meta.retryable = parsed.retryable
      if (typeof parsed.request_id === 'string') meta.request_id = parsed.request_id
    } catch {
      /* ignore */
    }
  }
  if (meta.statusCode === undefined) {
    const statusMatch = msg.match(/HTTP (\d+)/)
    if (statusMatch) meta.statusCode = parseInt(statusMatch[1], 10)
  }
  return meta
}
